import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { toNumberDisplayValue } from "@intentsify/utils";
import { OverflowableText } from "components/Text";
import { DonutSlice } from "./PieChart.definition";

type DonutLegendProps = {
  slices: DonutSlice[];
  hiddenLabels: string[];
  onToggle: (label: string) => void;
  /** Show only each slice's share; otherwise the value and its share. */
  displayAsPercent: boolean;
};

export const formatShare = (share: number) => `${Math.round(share * 100)}%`;

/** Legend rows as in the PDF reports: swatch, label, right-aligned value. */
export const DonutLegend = ({
  slices,
  hiddenLabels,
  onToggle,
  displayAsPercent,
}: DonutLegendProps) => {
  const total = slices
    .filter((slice) => !hiddenLabels.includes(slice.label))
    .reduce((sum, slice) => sum + slice.value, 0);

  return (
    // minmax(0, 1fr) keeps long labels truncating instead of widening the
    // grid past the card and pushing the values out of view.
    <Grid w="100%" minW={0} templateColumns="minmax(0, 1fr)" rowGap={0.5}>
      {slices.map((slice) => {
        const isHidden = hiddenLabels.includes(slice.label);
        const share = total > 0 && !isHidden ? slice.value / total : 0;

        return (
          <Flex
            key={slice.label}
            as="button"
            type="button"
            aria-pressed={!isHidden}
            title={isHidden ? "Show in chart" : "Hide from chart"}
            alignItems="center"
            gap={2}
            px={2}
            py={1}
            borderRadius="md"
            fontSize="sm"
            textAlign="left"
            opacity={isHidden ? 0.4 : 1}
            _hover={{ bg: "blackAlpha.50", _dark: { bg: "whiteAlpha.100" } }}
            onClick={() => onToggle(slice.label)}
          >
            <Box
              w="10px"
              h="10px"
              flexShrink={0}
              borderRadius="sm"
              bg={slice.color}
            />
            <Box flex={1} minW={0}>
              <OverflowableText
                aria-label={slice.label}
                whiteSpace="nowrap"
                fontSize="sm"
              >
                {slice.label}
              </OverflowableText>
            </Box>
            <Text
              as="span"
              flexShrink={0}
              sx={{ fontVariantNumeric: "tabular-nums" }}
              color="gray.600"
              _dark={{ color: "gray.300" }}
            >
              {displayAsPercent
                ? formatShare(share)
                : `${toNumberDisplayValue(slice.value)} · ${formatShare(share)}`}
            </Text>
          </Flex>
        );
      })}
    </Grid>
  );
};
